import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  auth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "../services/auth";
import { logger } from "../utils/logger";
import { toast } from "react-toastify";
import { usePersistentState } from "../hooks/usePersistentState";
import { LOGIN_TEXT } from "../i18n/login";
import { useAuth } from "../context/AuthContext";

export default function LoginPage() {
  const { ssoChecking, ssoNoticeKey } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [language, setLanguage] = usePersistentState("kv_lang", "nl");

  const text = LOGIN_TEXT[language] || LOGIN_TEXT.nl;

  const normalizeAuthError = (error) => {
    const code = error?.code || "";
    switch (code) {
      case "auth/invalid-email":
        return text.invalidEmail;
      case "auth/user-disabled":
        return text.userDisabled;
      case "auth/user-not-found":
        return text.userNotFound;
      case "auth/wrong-password":
        return text.wrongPassword;
      case "auth/too-many-requests":
        return text.tooManyRequests;
      case "auth/invalid-credential":
        return text.invalidCredential;
      default:
        return text.loginFailed;
    }
  };

  const handlePasswordReset = async () => {
    setErrorMessage("");
    setResetSent(false);
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      toast.warn(text.fillEmailFirst);
      return;
    }

    try {
      auth.languageCode = language;
      setEmail(normalizedEmail);
      await sendPasswordResetEmail(auth, normalizedEmail);
      setResetSent(true);
      toast.success(text.resetSent);
    } catch (error) {
      const code = error?.code || "";
      const message =
        code === "auth/invalid-email"
          ? text.invalidEmail
          : code === "auth/user-not-found"
            ? text.userNotFound
            : code === "auth/too-many-requests"
              ? text.tooManyRequests
              : text.resetFailed;

      logger.error("Password reset mislukt", {
        code,
        email: normalizedEmail,
      });
      setErrorMessage(message);
      toast.error(message);
    }
  };

  const login = async () => {
    setErrorMessage("");
    setLoading(true);

    try {
      if (!email.trim() || !password) {
        const invalidCredential = new Error("Invalid credential");
        invalidCredential.code = "auth/invalid-credential";
        throw invalidCredential;
      }

      await signInWithEmailAndPassword(auth, email.trim(), password);
      navigate("/", { replace: true });
    } catch (error) {
      const message = normalizeAuthError(error);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-6 rounded shadow">
      <h2 className="text-xl font-bold mb-4">{text.title}</h2>

      <div className="flex gap-2 mb-4">
        {[
          ["nl", "NL"],
          ["en", "English"],
          ["pl", "Polska"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            disabled={loading}
            onClick={() => setLanguage(value)}
            className={`px-3 py-1 rounded ${
              language === value ? "bg-green-700 text-white" : "bg-gray-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {ssoChecking && (
        <div className="mb-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm font-semibold text-green-800">
          {text.ssoChecking}
        </div>
      )}

      {!!ssoNoticeKey && !!text.ssoNotices?.[ssoNoticeKey] && (
        <div
          className="mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          role="status"
        >
          {text.ssoNotices[ssoNoticeKey]}
        </div>
      )}

      {loading && (
        <div className="mb-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm font-semibold text-green-800">
          {text.loggingIn}
        </div>
      )}

      <label className="block mb-2">{text.email}</label>
      <input
        disabled={loading}
        type="email"
        autoComplete="username"
        className="border p-1 w-full mb-2"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />

      <label className="block mb-2">{text.password}</label>
      <input
        disabled={loading}
        type="password"
        autoComplete="current-password"
        className="border p-1 w-full mb-4"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !loading) login();
        }}
      />

      <button
        type="button"
        onClick={handlePasswordReset}
        disabled={loading}
        className="text-sm text-blue-700 underline mb-3"
      >
        {text.forgotPassword}
      </button>

      {!!errorMessage && (
        <div className="text-red-600 text-sm mb-2">{errorMessage}</div>
      )}
      {resetSent && (
        <div className="text-green-700 text-sm mb-2">{text.resetSent}</div>
      )}

      <button
        type="button"
        onClick={login}
        disabled={loading}
        className={`bg-green-700 text-white px-3 py-2 rounded w-full ${
          loading ? "opacity-60 cursor-not-allowed" : ""
        }`}
      >
        {loading ? text.loading : text.login}
      </button>
    </div>
  );
}
